import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";

export function HireChefDialog({ chef, open, onClose }) {
  const [form, setForm] = useState({
    contact_name: "", contact_email: "", event_date: "",
    hours: 4, notes: "", budget: "",
  });
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async () => {
    if (!form.contact_name || !form.contact_email || !form.event_date) {
      toast.error("Name, email and date are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/chef-hire", {
        chef_id: chef.id,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        event_date: new Date(form.event_date).toISOString(),
        hours: Number(form.hours),
        notes: form.notes,
        budget: form.budget ? Number(form.budget) : null,
      });
      toast.success("Request sent — the chef will be in touch");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={onClose} data-testid="hire-dialog">
      <div className="bg-[#0a0a0a] border hairline max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="tick-label">Hire chef</div>
            <h2 className="font-display font-black text-3xl tracking-tighter mt-1">{chef.name}</h2>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-white"><X size={20} /></button>
        </div>
        <p className="text-neutral-400 text-sm mt-3">
          Send a request — the chef will pick a suitable kitchen and reply directly to your email.
        </p>

        <div className="mt-6 space-y-4">
          <Row label="Your name">
            <input data-testid="hire-name" value={form.contact_name} onChange={(e) => setForm({...form, contact_name: e.target.value})} className="w-full border hairline px-3 py-2.5 focus:outline-none focus:border-[#FF3B30]" />
          </Row>
          <Row label="Contact email">
            <input data-testid="hire-email" type="email" value={form.contact_email} onChange={(e) => setForm({...form, contact_email: e.target.value})} className="w-full border hairline px-3 py-2.5 focus:outline-none focus:border-[#FF3B30]" />
          </Row>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Event date">
              <input data-testid="hire-date" type="datetime-local" value={form.event_date} onChange={(e) => setForm({...form, event_date: e.target.value})} className="w-full border hairline px-3 py-2.5 focus:outline-none focus:border-[#FF3B30]" />
            </Row>
            <Row label="Hours needed">
              <input data-testid="hire-hours" type="number" min={1} value={form.hours} onChange={(e) => setForm({...form, hours: e.target.value})} className="w-full border hairline px-3 py-2.5 focus:outline-none focus:border-[#FF3B30]" />
            </Row>
          </div>
          <Row label="Budget (optional)">
            <input data-testid="hire-budget" type="number" placeholder="$" value={form.budget} onChange={(e) => setForm({...form, budget: e.target.value})} className="w-full border hairline px-3 py-2.5 focus:outline-none focus:border-[#FF3B30]" />
          </Row>
          <Row label="Notes">
            <textarea data-testid="hire-notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={3} placeholder="Cuisine, guests, dietary needs…" className="w-full border hairline px-3 py-2.5 focus:outline-none focus:border-[#FF3B30]" />
          </Row>
        </div>

        <div className="mt-6 flex gap-2">
          <button data-testid="hire-submit" onClick={submit} disabled={busy} className="btn-signal flex-1 disabled:opacity-50">
            {busy ? "..." : "Send request"}
          </button>
          <button onClick={onClose} className="btn-outline">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <div className="tick-label mb-1">{label}</div>
      {children}
    </div>
  );
}
